// 📄 components/Configuracion/ConfiguracionTransaccionesZ.tsx
// Gestión de las transacciones Z (desarrollos SAP personalizados) propias
// de ESTE proyecto — piloto, mismo patrón estructural que
// ConfiguracionPlantillasXml.tsx (lista + modal alta/edición + ConfirmDialog
// para borrar), pero el guardado es DIRECTO contra Firestore (ver
// transaccionZService.ts) con toast de confirmación en vez de un botón
// "guardar configuración" al final — mismo patrón que ConfiguracionApis.tsx.
import { useEffect, useState } from "react";
import { Terminal, Plus, RefreshCw, Pencil, Trash2, Info } from "lucide-react";
import { useAuth } from "../../context/AuthContext.tsx";
import {
  getTransaccionesZ, eliminarTransaccionZ, TransaccionZ
} from "../../services/transaccionZService.ts";
import { categoriaTransaccionMeta, solucionTransaccionMeta } from "../../services/catalogoTransaccionesService.ts";
import ConfirmDialog from "../ui/ConfirmDialog.tsx";
import Alert from "../ui/Alert.tsx";
import TransaccionZFormModal from "./TransaccionZFormModal.tsx";

interface ConfiguracionTransaccionesZProps {
  projectId: string;
  projectNombre?: string;
}

export default function ConfiguracionTransaccionesZ({ projectId, projectNombre }: ConfiguracionTransaccionesZProps) {
  // Borrar está restringido a Admin en firestore.rules (mismo patrón que
  // incidentes) — se oculta el botón para el resto de los miembros en vez
  // de mostrar una acción que siempre fallaría con permission-denied.
  const { profile } = useAuth();
  const esAdmin = profile?.role === "admin";
  const [transacciones, setTransacciones] = useState<TransaccionZ[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [modalOpen, setModalOpen] = useState(false);
  const [editando, setEditando] = useState<TransaccionZ | null>(null);
  const [aEliminar, setAEliminar] = useState<TransaccionZ | null>(null);
  const [eliminando, setEliminando] = useState(false);

  const [alertMessage, setAlertMessage] = useState("");
  const [alertType, setAlertType] = useState<"success" | "error" | "warning">("success");
  const showAlert = (msg: string, type: "success" | "error" | "warning" = "success") => {
    setAlertMessage(msg);
    setAlertType(type);
    setTimeout(() => setAlertMessage(""), 4000);
  };

  const cargar = async () => {
    setLoading(true);
    try {
      const data = await getTransaccionesZ(projectId);
      setTransacciones(data);
    } catch (err) {
      console.error("Error cargando las transacciones Z:", err);
      setError("No se pudieron cargar las transacciones Z de este proyecto.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!projectId) { setLoading(false); return; }
    cargar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  const handleGuardado = () => {
    const eraEdicion = !!editando;
    setModalOpen(false);
    setEditando(null);
    cargar();
    showAlert(eraEdicion ? "Transacción Z actualizada correctamente." : "Transacción Z guardada correctamente.");
  };

  const handleCancelarModal = () => {
    setModalOpen(false);
    setEditando(null);
  };

  const handleConfirmarEliminar = async () => {
    if (!aEliminar || eliminando) return;
    setEliminando(true);
    try {
      await eliminarTransaccionZ(projectId, aEliminar.id);
      setAEliminar(null);
      cargar();
      showAlert("Transacción Z eliminada correctamente.");
    } catch (err: any) {
      showAlert(err.message || "Error al eliminar la transacción Z.", "error");
    } finally {
      setEliminando(false);
    }
  };

  if (loading) {
    return (
      <div className="p-10 text-center text-gray-400 flex flex-col items-center gap-3">
        <RefreshCw className="w-6 h-6 animate-spin text-blue-500" />
        <p className="text-sm">Cargando transacciones Z...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Alert message={alertMessage} type={alertType} onClose={() => setAlertMessage("")} />

      {error && (
        <div className="bg-red-500/10 border border-red-500/30 text-red-400 p-3 rounded-lg text-xs">{error}</div>
      )}

      <div className="bg-blue-500/10 border border-blue-500/20 text-blue-300 p-3 rounded-lg text-xs flex items-start gap-2">
        <Info className="w-4 h-4 flex-shrink-0 mt-0.5" />
        <span>Transacciones Z de este proyecto — códigos de transacción personalizados desarrollados para este cliente. Solo visibles dentro de este proyecto.</span>
      </div>

      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-gray-300">Transacciones Z</h3>
        <button
          type="button"
          onClick={() => setModalOpen(true)}
          className="flex items-center gap-1.5 text-xs font-semibold text-blue-400 hover:text-blue-300 transition-colors"
        >
          <Plus className="w-3.5 h-3.5" /> Nueva transacción Z
        </button>
      </div>

      {transacciones.length === 0 ? (
        <div className="p-10 text-center text-gray-500 bg-white/5 border border-white/10 rounded-2xl">
          <Terminal className="w-8 h-8 mx-auto mb-2 text-gray-600" />
          <p className="text-sm">Este proyecto todavía no tiene transacciones Z propias.</p>
        </div>
      ) : (
        <div className="space-y-2.5">
          {transacciones.map((t) => {
            const catMeta = categoriaTransaccionMeta(t.categoria);
            const solMeta = solucionTransaccionMeta(t.solucion);
            return (
              <div key={t.id} className="flex items-start gap-4 bg-white/5 border border-white/10 rounded-2xl p-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-mono font-semibold text-white">{t.codigo}</span>
                    <span className="text-xs text-gray-400 truncate">{t.nombre}</span>
                  </div>
                  <div className="flex items-center gap-1.5 flex-wrap mt-1.5">
                    <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full border text-purple-300 bg-purple-500/10 border-purple-500/30">
                      {catMeta.label}
                    </span>
                    <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full border text-indigo-300 bg-indigo-500/10 border-indigo-500/30">
                      {solMeta.label}
                    </span>
                    {t.sistemasDisponibles && t.sistemasDisponibles.map((s) => (
                      <span key={s} className="text-[9px] font-bold uppercase px-1.5 py-0.5 rounded border text-sky-300 bg-sky-500/10 border-sky-500/30">
                        {s}
                      </span>
                    ))}
                  </div>
                </div>
                <button
                  onClick={() => setEditando(t)}
                  className="p-2 rounded-lg text-gray-400 hover:text-white hover:bg-white/10 transition-all flex-shrink-0"
                  title="Editar"
                >
                  <Pencil className="w-4 h-4" />
                </button>
                {esAdmin && (
                  <button
                    onClick={() => setAEliminar(t)}
                    className="p-2 rounded-lg text-gray-400 hover:text-red-400 hover:bg-red-500/10 transition-all flex-shrink-0"
                    title="Eliminar"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}

      {(modalOpen || editando) && (
        <TransaccionZFormModal
          projectId={projectId}
          transaccionInicial={editando}
          onGuardado={handleGuardado}
          onCancelar={handleCancelarModal}
        />
      )}

      {aEliminar && (
        <ConfirmDialog
          title="¿Eliminar esta transacción Z?"
          message={`"${aEliminar.codigo} — ${aEliminar.nombre}" se eliminará de ${projectNombre || "este proyecto"}. Esta acción no se puede deshacer.`}
          confirmLabel={eliminando ? "Eliminando..." : "Eliminar"}
          danger
          onConfirm={handleConfirmarEliminar}
          onCancel={() => setAEliminar(null)}
        />
      )}
    </div>
  );
}
